import {useField, useFormikContext} from 'formik'
import React, {useCallback, useMemo, useState} from 'react'
import MaskedInput from 'react-text-mask'
import {createNumberMask} from 'text-mask-addons'
import ComponentCheckbox from '../ComponentCheckbox'
import {useHook} from '../helpers'
import {useIntl} from 'react-intl'
import {TComponentInput, TMaskedStringField, TNumberField, TStringField} from './_types'

const getInputType = (field_type: string, showPassword?: boolean) => {
    if (showPassword) {
        return 'text'
    } else {
        switch (field_type) {
            case 'email':
            case 'password':
                return field_type
            case 'year':
                return 'number'
            case 'string':
            default:
                return 'text'
        }
    }
}

const StringField = React.memo<TStringField>((props) => {
    const {
        resolvedClassName,
        field_type,
        name,
        value,
        handleChange,
        handleBlur,
        is_disabled,
        placeholder,
        isError,
        error,
    } = props

    const [showPassword, setShowPassword] = useState(false)
    const intl = useIntl()

    return (
        <>
            <div className='componentInput_wrapper'>
                <input
                    className={resolvedClassName}
                    type={getInputType(field_type, showPassword)}
                    name={name}
                    value={value ?? ''}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    disabled={is_disabled}
                    placeholder={placeholder}
                    autoComplete='new-password'
                    onWheel={(event) => event.currentTarget.blur()}
                />
                {field_type === 'password' ? (
                    <div className='componentInput_passwordCheckboxContainer'>
                        <ComponentCheckbox
                            customChecked={showPassword}
                            customHandler={() => setShowPassword((prev) => !prev)}
                            label={intl.formatMessage({id: 'INPUT.SHOW_PASSWORD_LABEL'})}
                        />
                    </div>
                ) : null}
            </div>
            {isError ? <div className='invalid_feedback'>{error}</div> : null}
        </>
    )
})

const NumberField = React.memo<TNumberField>((props) => {
    const {
        currencyMask,
        resolvedClassName,
        name,
        value,
        handleChange,
        handleBlur,
        is_disabled,
        placeholder,
        isError,
        error,
    } = props

    return (
        <>
            <div className='componentInput_wrapper'>
                <MaskedInput
                    mask={currencyMask}
                    className={resolvedClassName}
                    type='text'
                    name={name}
                    value={value ?? ''}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    disabled={is_disabled}
                    placeholder={placeholder}
                />
            </div>
            {isError ? <div className='invalid_feedback'>{error}</div> : null}
        </>
    )
})

const MaskedStringField = React.memo<TMaskedStringField>((props) => {
    const {
        mask,
        resolvedClassName,
        name,
        value,
        handleChange,
        handleBlur,
        is_disabled,
        placeholder,
        isError,
        error,
        pipe,
    } = props

    // Если маска отключена (false) - используем обычный input
    if (mask === false) {
        return (
            <>
                <div className='componentInput_wrapper'>
                    <input
                        className={resolvedClassName}
                        type='text'
                        name={name}
                        value={value ?? ''}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        disabled={is_disabled}
                        placeholder={placeholder}
                    />
                </div>
                {isError ? <div className='invalid_feedback'>{error}</div> : null}
            </>
        )
    }

    return (
        <>
            <div className='componentInput_wrapper'>
                <MaskedInput
                    mask={mask}
                    className={resolvedClassName}
                    type='text'
                    name={name}
                    value={value ?? ''}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    disabled={is_disabled}
                    placeholder={placeholder}
                    guide={false}
                    pipe={pipe}
                />
            </div>
            {isError ? <div className='invalid_feedback'>{error}</div> : null}
        </>
    )
})

const ComponentInput: React.FC<TComponentInput> = (props) => {
    const {
        article,
        field_type,
        is_disabled,
        hook,
        onBlurSubmit,
        placeholder,
        className,
        suffix,
        customHandler,
    } = props
    const {values, setFieldValue, handleSubmit} = useFormikContext<any>()
    const [{name, value, onBlur}, {error, touched}] = useField(article)
    const isMaskedInput = field_type === 'integer' || field_type === 'float'
    const isMaskedStringInput =
        field_type === 'inn' ||
        field_type === 'passport_series' ||
        field_type === 'passport_number' ||
        field_type === 'vin' ||
        field_type === 'birth_certificate'
    const isError = Boolean(error && touched)
    const resolvedClassName = `form-control form-control-solid${
        isError ? ' invalid' : ''
    } ${className}`

    const setValueForHook = useHook({
        article: name,
        values,
        setFieldValue,
        handleSubmit,
        hook,
        isFilter: onBlurSubmit,
    })
    const isUseBasicSubmit = onBlurSubmit && !hook

    const currencyMask = useMemo(() => {
        if (!isMaskedInput) {
            return null
        } else {
            const allowDecimal = field_type === 'float'
            const allowNegative = field_type === 'integer'
            const defaultMaskOptions = {
                prefix: '',
                suffix: suffix ?? '',
                includeThousandsSeparator: true,
                thousandsSeparatorSymbol: ' ',
                allowDecimal,
                decimalSymbol: '.',
                decimalLimit: 2,
                //integerLimit: 7,
                allowNegative,
                allowLeadingZeroes: false,
            }
            return createNumberMask({
                ...defaultMaskOptions,
            })
        }
    }, [isMaskedInput, field_type, suffix])

    // Маски для строковых полей с масками
    const stringMask = useMemo(() => {
        if (!isMaskedStringInput) {
            return null
        }

        switch (field_type) {
            case 'inn':
                // ИНН: 10 или 12 цифр (маска на 12, можно вводить 10)
                return [/\d/, /\d/, /\d/, /\d/, /\d/, /\d/, /\d/, /\d/, /\d/, /\d/, /\d/, /\d/]

            case 'passport_series':
                // Серия паспорта: 4 цифры
                return [/\d/, /\d/, /\d/, /\d/]

            case 'passport_number':
                // Номер паспорта: 6 цифр
                return [/\d/, /\d/, /\d/, /\d/, /\d/, /\d/]

            case 'vin':
                // VIN: 17 символов (A-Z, 0-9, исключая I, O, Q)
                const vinChar = /[A-HJ-NPR-Z0-9]/i
                return [
                    vinChar,
                    vinChar,
                    vinChar,
                    vinChar,
                    vinChar,
                    vinChar,
                    vinChar,
                    vinChar,
                    vinChar,
                    vinChar,
                    vinChar,
                    vinChar,
                    vinChar,
                    vinChar,
                    vinChar,
                    vinChar,
                    vinChar,
                ]

            case 'birth_certificate':
                return false // Отключаем маску, форматирование делаем в handleChange

            default:
                return null
        }
    }, [isMaskedStringInput, field_type])

    // Pipe функция для ИНН - позволяет вводить 10 или 12 цифр
    const innPipe = useCallback((value: string) => {
        // Убираем все нецифровые символы
        const digits = value.replace(/\D/g, '')

        // Если 10 или 12 цифр - возвращаем как есть
        if (digits.length === 10 || digits.length === 12) {
            return digits
        }

        // Если меньше 10 - возвращаем текущее значение (пользователь еще вводит)
        if (digits.length < 10) {
            return value
        }

        // Если больше 12 - обрезаем до 12
        if (digits.length > 12) {
            return digits.slice(0, 12)
        }

        // Если 11 цифр - возвращаем как есть (пользователь вводит 12-ю)
        return value
    }, [])

    const normalizeInputValue = (v: any): string | number | '' => {
        if (v === null || v === undefined) return ''

        if (typeof v === 'string' || typeof v === 'number') return v

        // часто прилетает list-значение: {title, value}
        if (typeof v === 'object' && v) {
            if ('value' in v && (typeof v.value === 'string' || typeof v.value === 'number')) {
                return v.value
            }
            if ('title' in v && typeof v.title === 'string') {
                return v.title
            }
        }

        // часто прилетает массив list-значений: [{title, value}]
        if (Array.isArray(v)) {
            if (!v.length) return ''
            const first = v[0]

            if (typeof first === 'string' || typeof first === 'number') return first

            if (first && typeof first === 'object') {
                if (
                    'value' in first &&
                    (typeof (first as any).value === 'string' ||
                        typeof (first as any).value === 'number')
                ) {
                    return (first as any).value
                }
                if ('title' in first && typeof (first as any).title === 'string') {
                    return (first as any).title
                }
            }

            return String(first)
        }

        return String(v)
    }

    const handleChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        if (customHandler) {
            return customHandler(event)
        } else {
            const inputValue = event.target.value

            if (inputValue === '') {
                return setFieldValue(name, null)
            }

            let resolvedValue: any = inputValue

            if (isMaskedInput) {
                if (field_type === 'integer') {
                    let cleaned = inputValue.replace(/[^\d-]/g, '')
                    if (cleaned.includes('-') && cleaned[0] !== '-')
                        cleaned = cleaned.replace(/-/g, '')
                    resolvedValue = cleaned === '-' ? null : parseInt(cleaned, 10)
                } else {
                    resolvedValue = Number(inputValue.replace(/[^\d.]/g, ''))
                }
            } else if (isMaskedStringInput) {
                switch (field_type) {
                    case 'vin':
                        resolvedValue = inputValue.toUpperCase()
                        break
                    case 'birth_certificate':
                        // Автоматическое форматирование свидетельства о рождении
                        // Убираем все служебные символы для анализа
                        const cleanValue = inputValue.replace(/[-\s№]/g, '')

                        let romanPart = ''
                        let cyrillicPart = ''
                        let digitPart = ''
                        let hasCyrillicInput = false

                        // Разбираем введенные символы по порядку
                        for (let i = 0; i < cleanValue.length; i++) {
                            const char = cleanValue[i]
                            // Если это римская цифра и еще не начали вводить кириллицу
                            if (
                                /[IVXLCDM]/i.test(char) &&
                                romanPart.length < 6 &&
                                !hasCyrillicInput
                            ) {
                                romanPart += char.toUpperCase()
                            }
                            // Если это кириллица и уже есть римские цифры
                            else if (
                                /[А-Яа-яЁё]/.test(char) &&
                                romanPart.length > 0 &&
                                cyrillicPart.length < 2
                            ) {
                                hasCyrillicInput = true
                                cyrillicPart += char.toUpperCase()
                            }
                            // Если это цифра и уже есть 2 кириллических символа
                            else if (
                                /\d/.test(char) &&
                                cyrillicPart.length === 2 &&
                                digitPart.length < 6
                            ) {
                                digitPart += char
                            }
                        }

                        // Формируем результат с автоматической подстановкой дефиса и №
                        if (romanPart.length > 0) {
                            let result = romanPart

                            // Дефис добавляем СРАЗУ после первой римской цифры
                            result += '-'

                            if (cyrillicPart.length > 0) {
                                result += cyrillicPart

                                // Если есть 2 кириллических символа и хотя бы одна цифра - добавляем пробел и №
                                if (cyrillicPart.length === 2 && digitPart.length > 0) {
                                    result += ' №' + digitPart
                                }
                            }

                            resolvedValue = result
                        } else {
                            resolvedValue = inputValue.trim()
                        }
                        break
                    default:
                        resolvedValue = inputValue.trim()
                }
            } else {
                switch (name) {
                    case 'first_name':
                    case 'last_name':
                    case 'patronymic':
                    case 'taxpayer_last_name':
                    case 'taxpayer_first_name':
                    case 'taxpayer_patronymic':
                    case 'present_last_name':
                    case 'present_first_name':
                    case 'present_patronymic':
                        resolvedValue = inputValue.includes('-')
                            ? inputValue
                                  .split('-')
                                  .map((str) =>
                                      str === '-'
                                          ? str
                                          : str.charAt(0).toUpperCase() + str.slice(1).toLowerCase()
                                  )
                                  .join('-')
                            : inputValue.charAt(0).toUpperCase() + inputValue.slice(1).toLowerCase()
                        break

                    case 'email':
                        resolvedValue = inputValue.replace(/[А-Яа-яЁё]/g, '').toLowerCase()
                        break

                    case 'password':
                        resolvedValue = inputValue.replace(/[А-Яа-яЁё]/g, '')
                        break

                    default:
                        resolvedValue = inputValue
                }
            }

            return setFieldValue(name, resolvedValue)
        }
    }, [])

    const handleBlur = useCallback((event: React.FocusEvent<HTMLInputElement>) => {
        let resolvedValue: any
        if (isMaskedInput) {
            resolvedValue = Number(event?.target.value.replace(/[^\d.-]/g, ''))
        } else if (isMaskedStringInput) {
            // Для полей с масками сохраняем как строку
            resolvedValue = event.target.value.trim()
        } else {
            resolvedValue = event.target.value
        }

        if (hook) {
            setValueForHook(resolvedValue)
        }

        onBlur(event)

        if (isUseBasicSubmit) {
            handleSubmit()
        }
    }, [])

    if (isMaskedInput) {
        return (
            <NumberField
                resolvedClassName={resolvedClassName}
                name={name}
                value={value}
                handleChange={handleChange}
                handleBlur={handleBlur}
                isError={isError}
                error={error}
                is_disabled={is_disabled}
                placeholder={placeholder}
                currencyMask={currencyMask}
            />
        )
    } else if (isMaskedStringInput) {
        return (
            <MaskedStringField
                resolvedClassName={resolvedClassName}
                name={name}
                value={value}
                handleChange={handleChange}
                handleBlur={handleBlur}
                isError={isError}
                error={error}
                is_disabled={is_disabled}
                placeholder={placeholder}
                mask={stringMask}
                pipe={field_type === 'inn' ? innPipe : undefined}
            />
        )
    } else {
        return (
            <StringField
                resolvedClassName={resolvedClassName}
                name={name}
                value={value}
                handleChange={handleChange}
                handleBlur={handleBlur}
                isError={isError}
                error={error}
                is_disabled={is_disabled}
                placeholder={placeholder}
                field_type={field_type}
            />
        )
    }
}

export default ComponentInput
